import React, { Fragment, useMemo } from 'react';
import { Tooltip } from 'antd';
import moment from 'moment';

import { DEFAULT_STAFF_FORM_SCHEMA, QUESTION_TYPES } from '../../../config/staffFormSchema';
import { hydrateValuesFromDetails, formatAnswer, hasValue } from '../../../utils/staffFormValues';
import StaffFormReadOnlySection from './StaffFormReadOnlySection';

// Imported here rather than by each page: this component is the only reason its consumers need the
// generated Tailwind sheet, so a new consumer cannot forget it and get unstyled cards.
import './staffApplication.generated.css';

/*
 * A submitted staff application, rendered as it stood when that person submitted it.
 *
 * Extracted from editCounselor.js so the director's personnel detail page and the counselor's own
 * /profile/information view cannot drift apart: both read the same counselordetail response, and
 * "what did this person actually fill in" must not have two answers depending on who is asking.
 *
 * Deliberately presentational and director-agnostic — it takes the API record and renders sections.
 * Everything role-specific (permissions toggles, resend-invite, folder assignment, the Edit button)
 * stays with whichever page owns it.
 *
 * Lives under staffApplication/ because tailwind.config.js only scans this directory, formBuilder/,
 * addCounselor*.js and config/staffFormSchema.js — tw- classes in a file outside those globs are
 * purged from the generated stylesheet. StaffFormReadOnlySection carries the same note.
 */

/*
 * The questions this person saw, which is the snapshot taken when they submitted — not the camp's
 * current form, so later edits, deletes and resets cannot rewrite how their record reads.
 *
 * Falls back to the local default only when there is no snapshot at all: a pre-versioning record
 * (staffFormVersion null, backfill not yet run) or a camp still on the legacy application, where
 * the server sends staffFormSnapshot: null.
 *
 * Exported because the detail page needs the same sections for its own legacy-signature check, and
 * a second copy of this fallback rule is exactly the kind of thing that goes stale.
 */
export const resolveSnapshotSections = details =>
    details?.staffFormSnapshot?.sections || DEFAULT_STAFF_FORM_SCHEMA;

const StaffApplicationReadOnly = ({ details }) => {
    // Same flat, questionKey-indexed shape the application itself fills in, so every question in
    // the schema — the camp's own custom ones included — resolves by key.
    const values = useMemo(() => hydrateValuesFromDetails(details), [details]);

    const sections = useMemo(() => resolveSnapshotSections(details), [details]);

    /*
     * Answers whose question isn't part of this person's snapshot — either added to the form after
     * they submitted and filled in later during an edit (sectionKey present, from the current
     * form's grouping), or a pre-versioning orphan with no home (sectionKey null/unrecognized).
     *
     * hasValue filters out an entry with nothing actually recorded, so an untouched question does
     * not manufacture a row here the way it legitimately does among the snapshot questions below.
     */
    const sectionKeys = useMemo(() => new Set(sections.map(section => section.sectionKey)), [sections]);
    const { extraAnswersBySection, unassignedExtraAnswers } = useMemo(() => {
        const bySection = {};
        const unassigned = [];
        (details?.customAnswers || []).forEach(entry => {
            if (entry.inSnapshot !== false || !hasValue(entry.answer)) return;
            if (entry.sectionKey && sectionKeys.has(entry.sectionKey)) {
                (bySection[entry.sectionKey] = bySection[entry.sectionKey] || []).push(entry);
            } else {
                unassigned.push(entry);
            }
        });
        return { extraAnswersBySection: bySection, unassignedExtraAnswers: unassigned };
    }, [details, sectionKeys]);

    /*
     * Which form this record came from. Rendered here rather than by each page so the director's
     * personnel screen and the counselor's own profile cannot disagree about it — the same reason
     * the answers themselves live in this component.
     *
     * Worded about the application rather than the reader, since a director reads it on someone
     * else's record. `answerVersions` beyond the stamp are later edits, by this person or by a
     * director; without naming them the line would imply the whole record came from one version.
     *
     * Hidden entirely when there is no stamp: a record predating versioning, or a camp still on
     * the legacy application. There is no honest number to show, and "version 1" would be a guess.
     *
     * `created` is the User record's own creation time, not a submission timestamp — but it is an
     * honest one to label "submitted" HERE specifically, because the stamp that gates this whole
     * block is only ever written by addCounselor/addStaff, the two paths that create the record
     * and its answers in the same request. A record made by inviteCounselor and filled in later
     * carries no stamp at all (updateCounselor deliberately never sets one), so it renders
     * nothing here rather than an invite date mislabelled as a submission.
     */
    const stamp = details?.staffFormVersion;
    const alsoEdited = (details?.answerVersions || []).filter(version => version !== stamp);
    const submittedOn = details?.created ? moment(details.created).format('MMM D, YYYY') : null;

    const renderValue = question => {
        // counselorProgram stores the program's id; programName is the label the applicant chose.
        if (question.questionKey === 'counselorProgram') return details?.programName || 'N/A';
        return formatAnswer(question, values[question.questionKey]);
    };

    /*
     * Built up front rather than mapped inline, because the version line is pinned to the FIRST
     * card and that is not necessarily sections[0] — a section whose questions were all hidden or
     * unanswered renders nothing, and the snapshot's leading section can be one of those.
     */
    const cards = [];
    sections.forEach(section => {
        // The snapshot already records `enabled` exactly as it stood at submit time, so
        // there is no "hidden but answered" case left to special-case — unlike the live
        // editable form, a question that was off when this person submitted was never
        // something they could have answered in the first place.
        const questions = section.questions.filter(question => (
            question.type !== QUESTION_TYPES.INFO && question.enabled
        ));
        const extras = extraAnswersBySection[section.sectionKey] || [];
        if (!questions.length && !extras.length) return;

        cards.push({
            section,
            rows: [
                ...questions.map(question => ({
                    key: question.questionKey,
                    label: question.label,
                    value: renderValue(question),
                })),
                ...extras.map(entry => ({
                    key: `extra-${entry.questionId}`,
                    label: entry.label,
                    value: formatAnswer(entry, entry.answer),
                    extra: true,
                })),
            ],
        });
    });

    if (unassignedExtraAnswers.length) {
        cards.push({
            section: {
                sectionKey: 'additional-answers',
                title: 'Additional answers',
                description: 'Recorded outside the standard application form.',
            },
            rows: unassignedExtraAnswers.map(entry => ({
                key: `extra-${entry.questionId}`,
                label: entry.label,
                value: formatAnswer(entry, entry.answer),
                extra: true,
            })),
        });
    }

    /*
     * Two lines of grey text in the card header competed with the section title for the same row.
     * Collapsed to a chip carrying just the version, with the sentence moved into a tooltip — the
     * number is what a director scans for, the provenance is what they occasionally ask about.
     *
     * antd for the same reasons VersionHint uses it: the card is `tw-overflow-hidden` so the
     * tooltip must be portal-rendered to avoid being clipped, and react-bootstrap's is pinned to
     * a Bootstrap 4 API against Bootstrap 5 CSS. `click` is in the trigger list because the
     * header has no hover on touch.
     */
    const versionChip = stamp ? (
        <Tooltip
            placement="bottomRight"
            trigger={['hover', 'click']}
            title={(
                <span>
                    <span className="tw-block">
                        Submitted{submittedOn ? ` on ${submittedOn}` : ''} using form version {stamp}
                    </span>
                    {alsoEdited.length ? (
                        <span className="tw-mt-1 tw-block">
                            Answers were also edited against {alsoEdited.map(version => `v${version}`).join(', ')}
                        </span>
                    ) : null}
                </span>
            )}>
            <button
                type="button"
                aria-label={`Submitted using form version ${stamp}`}
                /*
                 * `tw-border-solid` and `tw-font-sans` are load-bearing here, not decoration.
                 * This page renders outside `.staff-application-page`, so the scoped reset in
                 * staffApplication.tailwind.css never runs: without them `tw-border` sets a width
                 * against the UA's default `outset` style and the chip renders as a 3-D bevel in
                 * the UA's button font rather than a flat pill in Poppins.
                 */
                className="tw-inline-flex tw-cursor-help tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-sand-300 tw-bg-sand-50 tw-px-2.5 tw-py-1 tw-font-sans tw-text-xs tw-font-semibold tw-text-gray-600 tw-transition hover:tw-border-brand-300 hover:tw-bg-brand-50 hover:tw-text-brand-700">
                v{stamp}
            </button>
        </Tooltip>
    ) : null;

    return (
        <Fragment>
            {cards.map((card, index) => (
                <StaffFormReadOnlySection
                    key={card.section.sectionKey}
                    section={card.section}
                    rows={card.rows}
                    aside={index === 0 ? versionChip : undefined} />
            ))}
        </Fragment>
    );
};

export default StaffApplicationReadOnly;
